import { getDiscountTotalWithoutShippingCoupon, getFilteredDiscountTotal } from '@cart-services/core/cartService'
import { CountryCode } from '@constants'
import { DiscountItemType, type DiscountItem } from '@hooks-api/types/dataModel/coupon'
import { Box } from '@pets-ui/Box/Box'
import { Chip } from '@pets-ui/Chip/Chip'
import { Collapse } from '@pets-ui/Collapse/Collapse'
import { Divider } from '@pets-ui/Divider/Divider'
import { HStack } from '@pets-ui/HStack/HStack'
import { ArrowDownSmall, ArrowUpSmall } from '@pets-ui/Icons'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { formatPrice } from '@utils'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DetailItem } from '../DetailItem/DetailItem'
import { useToggleSections } from '../hooks/use-order-detail-toggle-sections'

const styles = stylex.create({
  amountInfo: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  clickable: {
    cursor: 'pointer',
  },
})

interface AmountDetailsProps {
  subtotal?: number
  discountTotal?: number
  shippingTotal?: number
  total?: number
  currency?: string
  rootStyle?: stylex.StyleXStyles
  discountItems?: DiscountItem[]
  reward_point_discount?: number
  referral_code_discount?: number
  showReferralCodeDiscount?: boolean
  countryCode?: CountryCode
  tax?: number
  addonV2Total?: number
  addonV2TotalBeforeDiscount?: number
}

export const AmountDetails = (props: AmountDetailsProps) => {
  const { t } = useTranslation()
  const {
    subtotal,
    shippingTotal,
    total,
    currency,
    rootStyle,
    discountItems,
    reward_point_discount = 0,
    referral_code_discount = 0,
    showReferralCodeDiscount = true,
    countryCode,
    addonV2Total,
    addonV2TotalBeforeDiscount,
    tax,
  } = props

  const singleCouponItems = useMemo(() =>
    discountItems
      ?.filter(item => !item.is_cart_level)
      .filter(item => item.type !== DiscountItemType.REWARD_POINT && item.type !== DiscountItemType.REFERRAL_CODE && item.type !== DiscountItemType.SHIPPING_COUPON && item.type !== DiscountItemType.ADDON_V2) || [],
  [discountItems])

  const { isOpen, toggle } = useToggleSections()

  const multiCouponItems = useMemo(() => discountItems?.filter(item => item.is_cart_level && item.type !== DiscountItemType.ADDON_V2) || [], [discountItems])
  const shippingCouponItems = useMemo(() => discountItems?.filter(item => item.type === DiscountItemType.SHIPPING_COUPON) || [], [discountItems])
  const hasDiscount = singleCouponItems.length > 0 || multiCouponItems.length > 0
  const discountTotalWithoutShippingCoupon = getDiscountTotalWithoutShippingCoupon(discountItems || [])
  const filteredDiscountTotal = getFilteredDiscountTotal(discountItems || [])
  const isUs = countryCode === CountryCode.US
  return (
    <VStack rootStyle={rootStyle}>
      <Text variant="subtitle">{t('orderDetail.transactionAmountSummary', { defaultValue: '交易金額摘要' })}</Text>
      <VStack gap={2} borderRadius={2}>
        <Box rootStyle={styles.amountInfo}>
          <Text>{t('orderDetail.items', { defaultValue: '商品總計' })}</Text>
          <Text>
            {formatPrice(currency, subtotal)}
          </Text>
        </Box>
        <VStack>
          <Box rootStyle={[styles.amountInfo, hasDiscount && styles.clickable]} onClick={hasDiscount ? () => toggle('discount') : undefined}>
            <HStack gap={1}>
              <Text>{t('orderDetail.promotions', { defaultValue: '優惠折扣' })}</Text>
              {hasDiscount && (
                isOpen('discount')
                  ? <ArrowUpSmall color={colors.grey600} fontSize={20} />
                  : <ArrowDownSmall color={colors.grey600} fontSize={20} />
              )}
            </HStack>
            <Text>
              {formatPrice(currency, filteredDiscountTotal, '-$value')}
            </Text>
          </Box>
          {hasDiscount && (
            <Collapse in={isOpen('discount')}>
              <VStack gap={1} mt={2} p={2} backgroundColor={colors.grey50} borderRadius={4}>
                {/* 單品 */}
                {singleCouponItems.length > 0
                && singleCouponItems?.map((item, index) => {
                  return (
                    <DetailItem
                      key={index}
                      label={t('orderDetail.itemLevelDiscount', { defaultValue: '單品' })}
                      title={item.display}
                      discount={item.discount_amount}
                      currency={currency}
                    />
                  )
                })}
                {/* 全單 */}
                {multiCouponItems.length > 0
                && multiCouponItems?.map((item, index) => {
                  return (
                    <DetailItem
                      key={index}
                      label={t('orderDetail.siteWideDiscount', { defaultValue: '全館' })}
                      title={item.display}
                      discount={item.discount_amount}
                      currency={currency}
                    />
                  )
                })}
              </VStack>
            </Collapse>
          )}
        </VStack>
        {!!addonV2Total && !!addonV2TotalBeforeDiscount && addonV2Total > 0 && addonV2TotalBeforeDiscount > 0 && (
          <Box rootStyle={styles.amountInfo}>
            <Text>{t('orderDetail.addon', { defaultValue: '加價購' })}</Text>
            <HStack gap={1}>
              <Text data-testid="text-subtotal-before-discount" color={colors.grey500} lineThrough>
                {`$${addonV2TotalBeforeDiscount.toLocaleString()}`}
              </Text>
              <Text data-testid="text-subtotal-after-discount">
                {`$${addonV2Total.toLocaleString()}`}
              </Text>
            </HStack>
          </Box>
        )}
        <VStack
          rootStyle={[styles.amountInfo, shippingCouponItems.length > 0 && styles.clickable]}
          onClick={shippingCouponItems.length > 0 ? () => toggle('shippingDiscount') : undefined}
        >
          <Box rootStyle={styles.amountInfo}>
            <HStack gap={1}>
              <Text>{t('orderDetail.shippingFee', { defaultValue: '運費' })}</Text>
              {shippingCouponItems.length > 0 && (
                isOpen('shippingDiscount')
                  ? <ArrowUpSmall color={colors.grey600} fontSize={20} />
                  : <ArrowDownSmall color={colors.grey600} fontSize={20} />
              )}
            </HStack>
            <Text data-testid="text-shipping-amount">
              {`$${shippingTotal?.toLocaleString()}`}
            </Text>
          </Box>
          {shippingCouponItems.length > 0
          && (
            <Collapse in={isOpen('shippingDiscount')}>
              <VStack gap={1} mt={2} p={2} backgroundColor={colors.grey50} borderRadius={4}>
                {shippingCouponItems.map((item, index) => {
                  return (
                    <DetailItem
                      key={index}
                      label={t('orderDetail.shippingFee', { defaultValue: '運費' })}
                      title={item.display}
                      discount={item.discount_amount}
                      currency={currency}
                      data-testid="text-shipping-discount-amount"
                    />
                  )
                })}
              </VStack>
            </Collapse>
          )}
        </VStack>
        {showReferralCodeDiscount && (
          <Box rootStyle={styles.amountInfo}>
            <Text>{t('orderDetail.pointsOrReferral', { defaultValue: '點數/好友推薦' })}</Text>
            <Text>
              {formatPrice(currency, Math.abs(reward_point_discount) + Math.abs(referral_code_discount), '-$value')}
            </Text>
          </Box>
        )}
        {isUs && (
          <HStack gap={1} justifyContent="space-between">
            <Text>{t('orderDetail.tax', { defaultValue: '稅額' })}</Text>
            <Text color={colors.grey800}>
              {formatPrice(currency, tax ?? 0)}
            </Text>
          </HStack>
        )}
        <Divider />
        <Box rootStyle={styles.amountInfo}>
          <Text variant="subtitle">{t('orderDetail.orderTotal', { defaultValue: '訂購總額' })}</Text>
          <HStack gap={2}>
            {discountTotalWithoutShippingCoupon > 0
            && (
              <Chip size="xSmall" isOutlined variant="square" color="green">
                {t('orderDetail.savedAmount', { amount: formatPrice(currency, discountTotalWithoutShippingCoupon), defaultValue: '已省下 {{amount}}' })}
              </Chip>
            )}
            <Text variant="h5" color={colors.red600}>
              {formatPrice(currency, total)}
            </Text>
          </HStack>
        </Box>
      </VStack>
    </VStack>
  )
}
